import { createSelector, createFeatureSelector, Action, combineReducers } from '@ngrx/store';

import { IAuroraState } from '@apps/aurora.state';

import * as fromMessageReducer from './message/message.reducer';
import * as fromAlertReducer from './alert/alert.reducer';
import { Alert } from '../domain/models';
import { Comparer } from '@common/services';
import { filterQuickSearch } from '@common/services/utils.service';
import { BackConstants } from '@apps/back/back.constants';
import { NotfAlertConstants } from '../notf.constants';
import { DateTime } from 'luxon';

export const NotfFeatureKey = 'notf';

export interface INotfState
{
    [fromMessageReducer.NotfMesgFeatureKey]: fromMessageReducer.INotfMesgState;
    [fromAlertReducer.NotfAlertFeatureKey]: fromAlertReducer.INotfAlertState;
}

export interface IState extends IAuroraState
{
    [NotfFeatureKey]: INotfState;
}

export function NotfReducers (state: INotfState | undefined, action: Action)
{
    return combineReducers({
        [fromMessageReducer.NotfMesgFeatureKey]: fromMessageReducer.NotfMesgReducer,
        [fromAlertReducer.NotfAlertFeatureKey]: fromAlertReducer.NotfAlertReducer
    })(state, action);
}

const getNotfState = createFeatureSelector<INotfState>(
    NotfFeatureKey
);

const getNotfWarnState = createSelector(
    getNotfState,
    (state: INotfState) => state[fromMessageReducer.NotfMesgFeatureKey]
);

const getNotfAlertState = createSelector(
    getNotfState,
    (state: INotfState) => state[fromAlertReducer.NotfAlertFeatureKey]
);

export const {
    selectAll: selectAllMessages
} = fromMessageReducer.adapter.getSelectors(getNotfWarnState);

export const notReadWarning = () => createSelector(
    selectAllMessages,
    (warnings) =>
        warnings.filter(w => !w.isRead)
);

export const counterNotReadWarning = () => createSelector(
    getNotfWarnState,
    (state: fromMessageReducer.INotfMesgState) =>
        state.entities
            .length
);

export const {
    selectAll: selectAllAlert,
    selectTotal: selectTotalAlert
} = fromAlertReducer.adapter.getSelectors(getNotfAlertState);

export const getAlertsToNotified =  createSelector(
    selectAllAlert,
    (alerts =>
    {
        const now = DateTime.utc();

        // La priorite est decidee ici et non par l'ordre d'arrivee du serveur : la collection est
        // egalement remplacee par la liste d'administration, que le backend trie par date de debut.
        return alerts.filter(a =>
            a.since && a.until && a.active && a.since < now && now < a.until
        ).sort(NotfAlertConstants.compareBySeverity);
    })
)

export const getFirstAlert = createSelector(
    getAlertsToNotified,
    (alerts: Alert[]): Alert | null =>
        alerts.length > 0 ? alerts[0] : null
);

export const getAllAlerts = createSelector(
    getNotfAlertState,
    (state) => state.alerts
);

export const getQuickSearchedAlerts = createSelector(
    getNotfAlertState,
    getAllAlerts,
    (state: fromAlertReducer.INotfAlertState, alerts: Alert[]) =>
        filterQuickSearch(alerts, state.qsWord, BackConstants.alertsQSFields)
);
export const getPipelinedAlerts = createSelector(
    getQuickSearchedAlerts,
    (alerts: Alert[], params: { comparer: Comparer<Alert> }) =>
        [...alerts].sort(params.comparer)
);

// export const getEntitiesExternalIsEndReached = createSelector(
//     getBackAuthState,
//     getTotalExternalEntities,
//     (state: fromAuthReducer.IBackAuthState, nbTotal: number) =>
//         nbTotal >= state.entities.external.counters.total
// );
